import { isAxiosError } from 'axios'

export type ApiErrorCode =
  | 'NETWORK_ERROR'
  | 'TIMEOUT'
  | 'CANCELLED'
  | 'BAD_REQUEST'
  | 'UNAUTHORIZED'
  | 'FORBIDDEN'
  | 'NOT_FOUND'
  | 'CONFLICT'
  | 'VALIDATION_ERROR'
  | 'SERVER_ERROR'
  | 'UNKNOWN'

interface ApiErrorOptions {
  code: ApiErrorCode
  message: string
  status?: number
  serverCode?: string
  serverMessage?: string
  details?: unknown
  cause?: unknown
}

/**
 * The single error type the rest of the app sees. Every failure coming out of
 * the API layer - HTTP errors, timeouts, network failures, malformed
 * responses - is normalized into an ApiError, so UI code can switch on
 * `code` and map it to a translated message (see useApiErrorMessage).
 */
export class ApiError extends Error {
  readonly code: ApiErrorCode
  readonly status: number | undefined
  /**
   * Machine-readable error key from the backend - primarily the X-Error-Key
   * response header (e.g. "account_blocked"), falling back to a key-shaped
   * body entry. Translated via common:serverErrors.* when present; also the
   * branch point for special flows (blocked account, page-limit upsell).
   */
  readonly serverCode: string | undefined
  /**
   * The backend's own localized human message (the error body is an array
   * of already-translated strings). Preferred over generic fallbacks when
   * no client-side translation exists for the serverCode.
   */
  readonly serverMessage: string | undefined
  readonly details: unknown

  constructor(options: ApiErrorOptions) {
    super(options.message, { cause: options.cause })
    this.name = 'ApiError'
    this.code = options.code
    this.status = options.status
    this.serverCode = options.serverCode
    this.serverMessage = options.serverMessage
    this.details = options.details
  }

  /** Transient failures that are worth retrying automatically. */
  get isRetryable(): boolean {
    return this.code === 'NETWORK_ERROR' || this.code === 'TIMEOUT' || this.code === 'SERVER_ERROR'
  }
}

function statusToCode(status: number): ApiErrorCode {
  switch (status) {
    case 400:
      return 'BAD_REQUEST'
    case 401:
      return 'UNAUTHORIZED'
    case 403:
      return 'FORBIDDEN'
    case 404:
      return 'NOT_FOUND'
    case 409:
      return 'CONFLICT'
    case 422:
      return 'VALIDATION_ERROR'
    default:
      return status >= 500 ? 'SERVER_ERROR' : 'UNKNOWN'
  }
}

/** snake_case machine keys, as opposed to localized sentences. */
const ERROR_KEY_PATTERN = /^[a-z0-9_]+$/

/**
 * First string carried by the error body, and which shape it came from. The
 * backend answers either with a bare JSON array of localized messages (older
 * builds put the error key itself there), or an ad-hoc `{error}` / `{message}`
 * object (some KYC/pages endpoints), which always carries a human sentence.
 */
function extractBodyText(data: unknown): { text: string; fromArray: boolean } | undefined {
  if (Array.isArray(data) && data.length > 0 && typeof data[0] === 'string') {
    return { text: data[0], fromArray: true }
  }
  if (typeof data === 'object' && data !== null) {
    const record = data as Record<string, unknown>
    for (const key of ['error', 'message']) {
      const value = record[key]
      if (typeof value === 'string' && value.length > 0) return { text: value, fromArray: false }
    }
  }
  return undefined
}

export function toApiError(error: unknown): ApiError {
  if (error instanceof ApiError) return error

  if (isAxiosError(error)) {
    if (error.code === 'ECONNABORTED' || error.code === 'ETIMEDOUT') {
      return new ApiError({ code: 'TIMEOUT', message: 'The request timed out', cause: error })
    }
    if (error.code === 'ERR_CANCELED') {
      return new ApiError({ code: 'CANCELLED', message: 'The request was cancelled', cause: error })
    }
    if (!error.response) {
      return new ApiError({
        code: 'NETWORK_ERROR',
        message: 'Unable to reach the server',
        cause: error,
      })
    }

    const { status, data, headers } = error.response
    // The machine key travels in X-Error-Key; the body carries the already
    // localized message. Older shapes put the key itself in the array body,
    // so a key-shaped array entry still counts as the code; an object body's
    // sentence never does, however short and lowercase it happens to be.
    const headerKey = typeof headers?.['x-error-key'] === 'string' ? headers['x-error-key'] : undefined
    const body = extractBodyText(data)
    const bodyIsKey = body !== undefined && body.fromArray && ERROR_KEY_PATTERN.test(body.text)
    const serverCode = headerKey ?? (bodyIsKey ? body.text : undefined)
    const serverMessage = bodyIsKey ? undefined : body?.text
    return new ApiError({
      code: statusToCode(status),
      message: serverMessage ?? serverCode ?? error.message,
      status,
      serverCode,
      serverMessage,
      details: data,
      cause: error,
    })
  }

  if (error instanceof Error) {
    return new ApiError({ code: 'UNKNOWN', message: error.message, cause: error })
  }

  return new ApiError({ code: 'UNKNOWN', message: 'An unknown error occurred', cause: error })
}

/**
 * The reaction rate limiter's pause (likes, follows, joins, friend requests):
 * the server's playful sentence is the whole story, so callers show it
 * instead of their generic failure text. Null for every other error.
 */
export function reactionPauseMessage(error: unknown): string | null {
  const apiError = toApiError(error)
  if (
    apiError.serverCode !== 'reaction_abuse_blocked' &&
    apiError.serverCode !== 'reaction_abuse_blocked_long'
  ) {
    return null
  }
  return apiError.serverMessage ?? apiError.message
}
